import { BadStatusError, cfetch } from '@confluence/network';

// Only the slice of the jira-metadata response this reader walks.
type JiraMetadataResponse = {
	groups?: ReadonlyArray<{
		type?: string | null;
		items?: ReadonlyArray<{ url?: string | null }> | null;
	}> | null;
};

// The endpoint also groups sprints and versions, which are not work items.
const WORK_ITEM_GROUP_TYPES = new Set(['ISSUES', 'EPICS']);

export type LinkedJiraWorkItemsErrorType = 'timeout' | 'badStatus' | 'parse' | 'network';

// `incomplete` is set when any Jira applink failed or timed out. `count` is -1 when the aggregate
// itself did not load in time.
type JiraMetadataAggregateResponse = {
	count?: number | null;
	incomplete?: boolean | null;
};

const getEndpoint = (contentId: string): string =>
	`/wiki/rest/jira-metadata/1.0/metadata?pageId=${encodeURIComponent(contentId)}`;

const getAggregateEndpoint = (contentId: string): string =>
	`/wiki/rest/jira-metadata/1.0/metadata/aggregate?pageId=${encodeURIComponent(contentId)}`;

export type EmptyLinkedJiraAggregate = { incomplete: boolean };

export type LinkedJiraAggregateObservation = {
	outcome: 'empty' | 'nonempty' | 'unavailable' | 'failed';
	incomplete?: boolean;
	error?: unknown;
};

export type LinkedJiraAggregateObserver = (result: LinkedJiraAggregateObservation) => void;

const observeAggregate = (
	observe: LinkedJiraAggregateObserver | undefined,
	result: LinkedJiraAggregateObservation,
): void => {
	try {
		observe?.(result);
	} catch {
		// A failed observation must not change whether the existing fallback runs.
	}
};

/**
 * Non-null only when the server-cached aggregate counts zero Jira links, so the uncached metadata
 * call can be skipped. A zero count still skips when `incomplete`: sites like Hello always have a
 * dead applink, which would otherwise make every page pay for the metadata fan-out. Any other
 * count, or a failed aggregate, resolves null. CCINT-9559
 */
export const fetchEmptyLinkedJiraAggregate = async (
	contentId: string,
	signal?: AbortSignal,
	observe?: LinkedJiraAggregateObserver,
): Promise<EmptyLinkedJiraAggregate | null> => {
	try {
		const response = await cfetch(getAggregateEndpoint(contentId), {
			method: 'GET',
			headers: { Accept: 'application/json' },
			credentials: 'include',
			signal,
		});
		const body: JiraMetadataAggregateResponse | null = await response.json();
		let outcome: LinkedJiraAggregateObservation['outcome'] = 'unavailable';
		if (body?.count === 0) {
			outcome = 'empty';
		} else if (typeof body?.count === 'number' && body.count > 0) {
			outcome = 'nonempty';
		}
		observeAggregate(observe, { outcome, incomplete: body?.incomplete === true });
		return body?.count === 0 ? { incomplete: body.incomplete === true } : null;
	} catch (error) {
		observeAggregate(observe, { outcome: 'failed', error });
		return null;
	}
};

/**
 * URLs of the Jira work items holding a remote link to this content, from the REST endpoint that
 * backs the page's "Jira links" byline. Stand-in for the graph while it does not reliably ingest
 * the work item -> page attachments AI Planner creates. CCINT-9346
 */
export const fetchLinkedJiraWorkItemUrls = async (
	contentId: string,
	signal?: AbortSignal,
): Promise<string[]> => {
	const response = await cfetch(getEndpoint(contentId), {
		method: 'GET',
		headers: { Accept: 'application/json' },
		credentials: 'include',
		signal,
	});
	const body: JiraMetadataResponse = await response.json();

	const urls = new Set<string>();
	(body.groups ?? []).forEach((group) => {
		if (!WORK_ITEM_GROUP_TYPES.has(group.type ?? '')) {
			return;
		}
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity -- linear in total item count; four fixed group types partition it
		(group.items ?? []).forEach((item) => {
			if (item.url) {
				urls.add(item.url);
			}
		});
	});
	return [...urls];
};

/** 4xx: anonymous viewer or no Jira app link. Expected, so not worth a monitoring report. */
export const isLinkedJiraWorkItemsClientError = (error: unknown): boolean =>
	BadStatusError.isBadStatusError(error) && error.response.status < 500;

export const classifyLinkedJiraWorkItemsError = (
	error: unknown,
	timedOut: boolean,
): LinkedJiraWorkItemsErrorType => {
	if (timedOut) {
		return 'timeout';
	}
	if (BadStatusError.isBadStatusError(error)) {
		return 'badStatus';
	}
	if (error instanceof SyntaxError) {
		return 'parse';
	}
	return 'network';
};
